// src/features/planning/pages/MasterScheduleRevisionEditorPage.tsx
// ADR-092 Rule PLN-FE-02/PLN-FE-04. Mirrors BomRevisionEditorPage
// (ADR-082): line editor for draft revisions, read-only render for
// published/superseded/archived, publish action inline.
//
// ⚠️ Hook names inferred from ADR-082 pattern — verify against real
// orval output before merging (ADR-092 Rule PLN-FE-06).

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useV1PlanningMasterScheduleRevisionsRetrieve,
  useV1PlanningMasterScheduleLinesCreate,
  useV1PlanningMasterScheduleRevisionsPublishCreate,
  useV1PlanningMasterScheduleRevisionsRunMrpCreate,
} from "@/api/generated/v1/v1";
import { useV1InventoryMaterialsList } from "@/api/generated/v1/v1";
import { useV1PlanningPeriodsList } from "@/api/generated/v1/v1";
import { RevisionStatusBadge } from "../components/RevisionStatusBadge";
import { PlanningPeriodPicker } from "../components/PlanningPeriodPicker";
import { usePermission } from "@/auth/usePermission";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { extractData, extractResults } from "@/shared/utils/apiResponseUtils";
import { PageBanner } from "@/shared/components/PageBanner";

export function MasterScheduleRevisionEditorPage() {
  const { revisionId } = useParams<{ revisionId: string }>();
  const navigate = useNavigate();
  const canManage = usePermission("planning.manage");

  const {
    data: revisionResp,
    isLoading,
    refetch,
  } = useV1PlanningMasterScheduleRevisionsRetrieve(revisionId!);
  const revision = extractData<any>(revisionResp);

  const { data: materialsData } = useV1InventoryMaterialsList();
  const materials = extractResults<any>(materialsData);

  const { data: periodsData } = useV1PlanningPeriodsList();
  const periods = extractResults<any>(periodsData);

  const getMaterialName = (id: string) => materials.find((m: any) => m.id === id)?.code || id;
  const getPeriodName = (id: string) => periods.find((p: any) => p.id === id)?.code || id;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      return new DateObject({ date: dateString, format: "YYYY-MM-DD" })
        .convert(persian, persian_fa)
        .format("YYYY/MM/DD");
    } catch {
      return dateString;
    }
  };

  const { mutateAsync: addLine, isPending: isAddingLine } =
    useV1PlanningMasterScheduleLinesCreate();
  const { mutateAsync: publish, isPending: isPublishing } =
    useV1PlanningMasterScheduleRevisionsPublishCreate();
  const { mutateAsync: runMrp, isPending: isRunningMrp } =
    useV1PlanningMasterScheduleRevisionsRunMrpCreate();

  const [materialFamilyId, setMaterialFamilyId] = useState<string | null>(
    null
  );
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [plannedQty, setPlannedQty] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  if (isLoading) return <p>در حال بارگذاری...</p>;
  if (!revision) return <p>نسخه یافت نشد.</p>;

  const isDraft = revision.status === "draft";

  async function handleAddLine(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!materialFamilyId || !periodId || !plannedQty) {
      setFormError("همهٔ فیلدها الزامی هستند.");
      return;
    }
    if (Number(plannedQty) < 0) {
      setFormError("مقدار برنامه‌ریزی‌شده نمی‌تواند منفی باشد.");
      return;
    }

    try {
      await addLine({
        data: {
          tenant_id: "",
          revision: revisionId!,
          material_family: materialFamilyId,
          period: periodId,
          planned_qty: plannedQty,
        },
      });
      setMaterialFamilyId(null);
      setPeriodId(null);
      setPlannedQty("");
      refetch();
    } catch (err) {
      setFormError(
        "افزودن خط با خطا مواجه شد — ممکن است این ترکیب ماده/بازه از قبل" +
          " در این نسخه ثبت شده باشد."
      );
    }
  }

  async function handlePublish() {
    if (!revision.lines.length) {
      setFormError("نمی‌توان نسخه‌ای بدون هیچ خطی را منتشر کرد.");
      return;
    }
    if (!window.confirm("انتشار این نسخه غیرقابل‌بازگشت است. ادامه می‌دهید؟")) {
      return;
    }
    await publish({ id: revisionId!, data: {} as any });
    refetch();
  }

  // ADR-094 Rule MRP-FE-01: only meaningful once the revision is published.
  async function handleRunMrp() {
    const rawResult = await runMrp({ id: revisionId!, data: {} as any });
    const result = extractData<any>(rawResult);
    navigate(`/planning/mrp-runs/${result?.id}`);
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`${revision.family_code} — نسخهٔ ${revision.revision_no}`}
        subtitle={`بازه: ${formatDate(revision.horizon_start)} تا ${formatDate(revision.horizon_end)}`}
        icon="✏️"
        helpLink="/help/planning/master-schedule-revision-editor"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <RevisionStatusBadge status={revision.status} />
          {isDraft && canManage && (
            <button
              onClick={handlePublish}
              disabled={isPublishing || revision.lines.length === 0}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#00b050",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: (isPublishing || revision.lines.length === 0) ? "not-allowed" : "pointer",
                opacity: (isPublishing || revision.lines.length === 0) ? 0.5 : 1,
                boxShadow: "0 4px 12px rgba(0, 176, 80, 0.2)",
                transition: "all 0.2s ease",
              }}
            >
              {isPublishing ? "در حال انتشار..." : "انتشار نسخه ✓"}
            </button>
          )}
          <button
            onClick={() => navigate(`/planning/master-schedules/${revision.family}`)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "1rem" }}>
          خطوط برنامه
        </h2>
        <table className="w-full text-sm text-left rtl:text-right border-collapse mb-8">
          <thead>
            <tr>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">ماده</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">بازهٔ زمانی</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">مقدار برنامه‌ریزی‌شده</th>
            </tr>
          </thead>
          <tbody>
            {revision.lines.map((line: any) => (
              <tr key={line.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-3 py-2.5">{getMaterialName(line.material_family)}</td>
                <td className="px-3 py-2.5">{getPeriodName(line.period)}</td>
                <td className="px-3 py-2.5">{line.planned_qty}</td>
              </tr>
            ))}
            {revision.lines.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-muted">
                  هنوز خطی ثبت نشده است.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ADR-092 Rule PLN-FE-04: add-line form only for draft + manage perm */}
        {isDraft && canManage && (
          <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid var(--glass-border)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem" }}>افزودن خط جدید</h3>
            <form onSubmit={handleAddLine} style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  ماده
                </label>
                <select
                  className="input-field"
                  style={{ width: "100%" }}
                  value={materialFamilyId ?? ""}
                  onChange={(e) => setMaterialFamilyId(e.target.value)}
                  required
                >
                  <option value="" disabled>انتخاب ماده</option>
                  {materials
                    .filter((m: any) => m.material_type === "semi_finished" || m.material_type === "finished_product")
                    .map((m: { id: string; code: string }) => (
                    <option key={m.id} value={m.id}>
                      {m.code}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  بازهٔ زمانی
                </label>
                <PlanningPeriodPicker value={periodId} onChange={setPeriodId} />
              </div>

              <div style={{ flex: 1, minWidth: "150px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  مقدار
                </label>
                <input
                  className="input-field"
                  style={{ width: "100%" }}
                  type="number"
                  min="0"
                  step="0.0001"
                  value={plannedQty}
                  onChange={(e) => setPlannedQty(e.target.value)}
                  required
                />
              </div>

              <button className="btn btn-primary" type="submit" disabled={isAddingLine} style={{ padding: "0.6rem 1.25rem", height: "42px" }}>
                {isAddingLine ? "..." : "افزودن خط"}
              </button>
            </form>
            {formError && <p style={{ color: "#ef4444", fontSize: "0.9rem", marginTop: "1rem" }}>{formError}</p>}
          </div>
        )}

        {!isDraft && (
          <p style={{ marginTop: "1rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            <em>
              این نسخه {revision.status === "published" ? "منتشر" : "بسته"}{" "}
              شده و غیرقابل‌ویرایش است.
            </em>
          </p>
        )}

        {/* ADR-094 Rule MRP-FE-01 */}
        {revision.status === "published" && canManage && (
          <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid var(--glass-border)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem" }}>برنامه‌ریزی نیاز مواد</h3>
            <button className="btn btn-primary" type="button" onClick={handleRunMrp} disabled={isRunningMrp}>
              {isRunningMrp ? "در حال اجرا..." : "⚙️ اجرای MRP روی این نسخه"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
